import { useEffect, useState } from "react";
import { History, Pencil, Plus, RefreshCw, Search, Tags, Trash2 } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import CatalogModal from "./CatalogModal";
import SupplierListingForm, { LISTINGS_ENDPOINT } from "./SupplierListingForm";
import { buttonClass, errorMessage, inputClass, PAGE_SIZE } from "./catalogUtils";

const STATUS_STYLES = {
  live: "bg-green-100 text-green-700",
  scheduled: "bg-blue-100 text-blue-700",
  expired: "bg-amber-100 text-amber-700",
  inactive: "bg-slate-100 text-slate-500",
};
const STATUS_LABELS = { live: "Live", scheduled: "Scheduled", expired: "Expired", inactive: "Inactive" };

function PriceHistory({ listing }) {
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ next: null, previous: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance.get(`${LISTINGS_ENDPOINT}${listing.id}/history/`, { params: { page, page_size: PAGE_SIZE } })
      .then(({ data }) => {
        if (cancelled) return;
        setEntries(data.results || []);
        setPagination({ next: data.next, previous: data.previous });
      })
      .catch((requestError) => { if (!cancelled) setError(errorMessage(requestError)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listing.id, page]);

  if (loading) return <p role="status" className="py-8 text-center text-sm text-slate-500">Loading price history...</p>;
  if (error) return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  if (!entries.length) return <p className="py-8 text-center text-sm text-slate-500">No price history yet.</p>;
  return (
    <div>
      <ol aria-label="Price history entries" className="divide-y divide-slate-100 rounded-lg border border-slate-100">
        {entries.map((entry) => (
          <li key={entry.id} className="p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-slate-900">{entry.change_type_label}</span>
              <span className="text-xs text-slate-500">{entry.changed_at} · {entry.changed_by_email || "System"}</span>
            </div>
            <p className="mt-1 text-slate-600">
              ₹{entry.trade_price} · {entry.discount}% off · GST {entry.gst_rate}% {entry.price_includes_gst ? "incl." : "extra"}
              {entry.min_order_quantity ? ` · MOQ ${entry.min_order_quantity}` : ""} · {entry.is_active ? "Active" : "Inactive"}
            </p>
            <p className="mt-1 text-xs text-slate-500">Valid {entry.valid_from} to {entry.valid_until}</p>
            {entry.changed_fields?.length > 0 && <p className="mt-1 text-xs text-slate-500">Changed: {entry.changed_fields.join(", ")}</p>}
            {entry.note && <p className="mt-1 text-xs italic text-slate-500">“{entry.note}”</p>}
          </li>
        ))}
      </ol>
      {(pagination.next || pagination.previous) && (
        <div className="mt-3 flex justify-between text-sm">
          <button disabled={!pagination.previous} onClick={() => setPage((value) => value - 1)} className={`${buttonClass} border border-slate-200`}>Newer</button>
          <button disabled={!pagination.next} onClick={() => setPage((value) => value + 1)} className={`${buttonClass} border border-slate-200`}>Older</button>
        </div>
      )}
    </div>
  );
}

function RenewForm({ busy, error, onSave, onClose }) {
  const [form, setForm] = useState({ valid_from: "", valid_until: "", note: "" });
  return (
    <form aria-label="Renew listing" onSubmit={(event) => { event.preventDefault(); onSave({ ...form, note: form.note.trim() }); }}>
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <p className="mb-4 text-sm text-slate-500">Set a new validity window. The listing is reactivated with its current prices.</p>
      <fieldset disabled={busy} className="grid gap-4 sm:grid-cols-2">
        {[["valid_from", "Valid from"], ["valid_until", "Valid until"]].map(([field, label], index) => (
          <div key={field}>
            <label htmlFor={`renew-${field}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            <input id={`renew-${field}`} type="datetime-local" required data-initial-focus={index === 0 ? true : undefined}
              value={form[field]} onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))} className={inputClass} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label htmlFor="renew-note" className="mb-1 block text-sm font-medium text-slate-700">Note (optional)</label>
          <input id="renew-note" maxLength={255} value={form.note}
            onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} className={inputClass} />
        </div>
      </fieldset>
      <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="submit" disabled={busy || !form.valid_from || !form.valid_until}
          className={`${buttonClass} bg-blue-500 text-white hover:bg-blue-600`}>{busy ? "Renewing..." : "Renew Listing"}</button>
        <button type="button" disabled={busy} onClick={onClose} className={`${buttonClass} border border-slate-200 text-slate-600 hover:bg-slate-50`}>Cancel</button>
      </div>
    </form>
  );
}

export default function CatalogSupplierPricing() {
  const [listings, setListings] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [listError, setListError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (search.trim() === query) return;
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListError("");
    axiosInstance.get(LISTINGS_ENDPOINT, { params: { q: query, status, page, page_size: PAGE_SIZE } })
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data.results) || typeof data.count !== "number") throw new Error("Invalid listing response");
        setListings(data.results);
        setPagination({ count: data.count, next: data.next, previous: data.previous });
      })
      .catch((requestError) => {
        if (!cancelled) {
          setListings([]);
          setListError(requestError.message === "Invalid listing response"
            ? "The server returned an invalid listing response. Please retry." : errorMessage(requestError));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, status, page, revision]);

  function refresh() {
    setPage(1);
    setRevision((value) => value + 1);
  }
  function openModal(next) {
    setModal(next);
    setDeleting(null);
    setError("");
    setNotice("");
  }
  function closeModal() {
    setModal(null);
    setError("");
  }
  async function run(request, message) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await request();
      setNotice(message);
      setModal(null);
      setDeleting(null);
      refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  async function editListing(listing) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axiosInstance.get(`${LISTINGS_ENDPOINT}${listing.id}/`);
      openModal({ type: "form", listing: data.data });
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  function saveListing(body) {
    const { listing } = modal;
    return run(
      () => (listing ? axiosInstance.patch(`${LISTINGS_ENDPOINT}${listing.id}/`, body) : axiosInstance.post(LISTINGS_ENDPOINT, body)),
      listing ? "Listing updated successfully." : "Listing created successfully.",
    );
  }

  const productLabel = (listing) => listing.product_detail?.name || "this listing";

  return (
    <div className="p-6 sm:px-[30px] sm:py-7">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900">Supplier Pricing</h1>
          <p className="mt-1 text-sm text-slate-500">Review and manage the trade prices suppliers offer on catalog products.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => openModal({ type: "form", listing: null })}
          className={`${buttonClass} flex items-center gap-2 bg-blue-500 text-white hover:bg-blue-600`}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Add Listing
        </button>
      </header>
      {listError && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{listError}</div>}
      {error && !modal && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">{notice}</div>}

      {modal?.type === "form" && (
        <CatalogModal title={modal.listing ? "Edit Listing" : "Add Listing"} titleId="listing-modal-title"
          closeLabel="Close listing modal" busy={busy} onClose={closeModal} wide>
          <SupplierListingForm listing={modal.listing} busy={busy} error={error} onSave={saveListing} onClose={closeModal} />
        </CatalogModal>
      )}
      {modal?.type === "renew" && (
        <CatalogModal title={`Renew ${productLabel(modal.listing)}`} titleId="renew-modal-title"
          closeLabel="Close renew modal" busy={busy} onClose={closeModal}>
          <RenewForm busy={busy} error={error} onClose={closeModal}
            onSave={(body) => run(() => axiosInstance.post(`${LISTINGS_ENDPOINT}${modal.listing.id}/renew/`, body), "Listing renewed successfully.")} />
        </CatalogModal>
      )}
      {modal?.type === "history" && (
        <CatalogModal title={`Price history: ${productLabel(modal.listing)}`} titleId="history-modal-title"
          closeLabel="Close price history" busy={false} onClose={closeModal} wide>
          <p className="mb-3 text-sm text-slate-500">Supplier: {modal.listing.vendor_name}</p>
          <PriceHistory listing={modal.listing} />
        </CatalogModal>
      )}
      {deleting && (
        <section aria-label="Confirm listing deactivation" className="mb-6 rounded-xl border border-red-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Deactivate {productLabel(deleting)} for {deleting.vendor_name}?</h2>
          <p className="my-2 text-sm text-slate-500">This is a soft delete. Buyers will stop seeing this price; the listing and its history remain saved. Edit the listing to reactivate it.</p>
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => run(() => axiosInstance.delete(`${LISTINGS_ENDPOINT}${deleting.id}/`), "Listing deactivated successfully.")}
              className={`${buttonClass} bg-red-600 text-white hover:bg-red-700`}>{busy ? "Deleting..." : "Confirm Delete"}</button>
            <button disabled={busy} onClick={() => { setDeleting(null); setError(""); }} className={`${buttonClass} border border-slate-200 text-slate-600`}>Cancel</button>
          </div>
        </section>
      )}

      <section aria-label="Supplier listings" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
            <input aria-label="Search listings" placeholder="Search supplier or product..." value={search}
              onChange={(event) => setSearch(event.target.value)} className={`${inputClass} pl-9`} />
          </div>
          <select aria-label="Listing status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        {loading ? <p role="status" className="p-12 text-center text-sm text-slate-500">Loading listings...</p> : listError ? (
          <div className="p-12 text-center"><button onClick={refresh} className={`${buttonClass} border border-slate-200 text-slate-600`}>Retry loading listings</button></div>
        ) : !listings.length ? (
          <div className="p-12 text-center text-slate-500"><Tags className="mx-auto mb-3 h-8 w-8 text-violet-400" aria-hidden="true" /><p className="text-sm">No supplier listings found.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-y border-slate-100 bg-slate-50 text-xs font-medium text-slate-500">
                <tr>{["Supplier", "Product / SKU", "Trade price", "Net price", "MOQ", "Validity", "Status", "Actions"].map((heading) => (
                  <th key={heading} className={`px-5 py-3 ${heading === "Actions" ? "text-right" : ""}`}>{heading}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {listings.map((listing) => (
                  <tr key={listing.id}>
                    <td className="px-5 py-4"><p className="font-medium text-slate-900">{listing.vendor_name}</p><p className="mt-1 text-xs text-slate-500">{listing.vendor_email}</p></td>
                    <td className="px-5 py-4"><p className="font-medium text-slate-900">{listing.product_detail?.name}</p><p className="mt-1 text-xs text-slate-500">{listing.product_detail?.sku}</p></td>
                    <td className="px-5 py-4 text-slate-500">
                      <p>₹{listing.trade_price}{Number(listing.discount) > 0 && <span className="ml-1 text-xs text-green-600">-{listing.discount}%</span>}</p>
                      <p className="mt-1 text-xs">GST {listing.gst_rate}% {listing.price_includes_gst ? "incl." : "extra"}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-500"><p className="font-medium text-slate-900">₹{listing.price_including_gst}</p><p className="mt-1 text-xs">₹{listing.price_excluding_gst} excl. GST</p></td>
                    <td className="px-5 py-4 text-slate-500">{listing.min_order_quantity ?? "-"}</td>
                    <td className="px-5 py-4 text-xs text-slate-500"><p>{listing.valid_from}</p><p className="mt-1">to {listing.valid_until}</p></td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[listing.status] || STATUS_STYLES.inactive}`}>{STATUS_LABELS[listing.status] || listing.status}</span></td>
                    <td className="px-5 py-4"><div className="flex justify-end gap-2">
                      <button aria-label={`Edit ${productLabel(listing)} for ${listing.vendor_name}`} disabled={busy} onClick={() => editListing(listing)}
                        className="rounded-lg border border-blue-200 p-2 text-blue-500 hover:bg-blue-50 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
                      <button aria-label={`Price history of ${productLabel(listing)} for ${listing.vendor_name}`} disabled={busy} onClick={() => openModal({ type: "history", listing })}
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-50"><History className="h-4 w-4" /></button>
                      {(listing.status === "expired" || listing.status === "inactive") && (
                        <button aria-label={`Renew ${productLabel(listing)} for ${listing.vendor_name}`} disabled={busy} onClick={() => openModal({ type: "renew", listing })}
                          className="rounded-lg border border-amber-200 p-2 text-amber-600 hover:bg-amber-50 disabled:opacity-50"><RefreshCw className="h-4 w-4" /></button>
                      )}
                      <button aria-label={`Delete ${productLabel(listing)} for ${listing.vendor_name}`} disabled={busy || !listing.is_active}
                        onClick={() => { setDeleting(listing); setModal(null); setError(""); setNotice(""); }}
                        className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !listError && pagination.count > 0 && (
          <nav aria-label="Listing pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 p-4 text-sm text-slate-500">
            <p>Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min((page - 1) * PAGE_SIZE + listings.length, pagination.count)} of {pagination.count} listings</p>
            <div className="flex items-center gap-3">
              <button disabled={!pagination.previous} onClick={() => setPage((value) => value - 1)} className={`${buttonClass} border border-slate-200`}>Previous</button>
              <span aria-live="polite">Page {page} of {Math.ceil(pagination.count / PAGE_SIZE)}</span>
              <button disabled={!pagination.next} onClick={() => setPage((value) => value + 1)} className={`${buttonClass} border border-slate-200`}>Next</button>
            </div>
          </nav>
        )}
      </section>
    </div>
  );
}
